/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseSoftwareTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import { useSelector } from "react-redux";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import EESWMSavedLinkageCard from "./EESWMSavedLinkageCard";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";

import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import { legalRequirements, userManagementApis } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";

function RaiseSoftwareTickets({
  savedLinkages,
  setSavedLinkages,
  entityType,
  onBeforeSmartMatch,
}) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);

  const [selectedSWMMain, setSelectedSWMMain] = useState(null);
  const [openEESelect, setOpenEESelect] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const [selectedLegalMain, setSelectedLegalMain] = useState(null);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledSWMComponents, setDisabledSWMComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTextCaseMain, setSelectedTestCaseMain] = useState(null);

  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestor] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  const [allUsersList, setAllUserList] = useState([]);
  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);
  // Prefill context coming from Smart Match
  const [prefillContext, setPrefillContext] = useState(null);

  const commitSelectedTestCases = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) {
      return;
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      testCases.forEach((testCase) => {
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            selectedItem?.test_case_id === testCase?.test_case_id,
        );
        if (!alreadySelected) {
          mergedSelection.push({ ...testCase, is_campaign_linked: true });
        }
      });

      setSelectedTestCaseForDisplay(mergedSelection);
      return mergedSelection;
    });
  };

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTextCaseMain || []).map((tc) => tc?.test_case_id),
    );
    const testCasesReadyForTicket = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked !== false ||
        currentSelectedIds.has(tc?.test_case_id),
    );
    const unlinkedTestCases = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked === false &&
        !currentSelectedIds.has(tc?.test_case_id),
    );

    setSelectedTestCaseMain(testCasesReadyForTicket);
    setSelectedTestCaseForDisplay(testCasesReadyForTicket);

    if (unlinkedTestCases.length > 0) {
      openCampaignDialogForTestCases(unlinkedTestCases);
    }
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(tag === ENTITY.TYPE.SWM || tag === ENTITY.TYPE.LEGAL);
    const componentName =
      tag === ENTITY.TYPE.SWM
        ? componentDetails?.description
        : selectedSWMMain?.description;
    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL
        ? ENTITY.TYPE.SWM
        : tag === ENTITY.TYPE.SWM
          ? ENTITY.TYPE.LEGAL
          : ENTITY.TYPE.LEGAL;

    const sourceId =
      tag === ENTITY.TYPE.LEGAL
        ? componentDetails?.swm_id
        : componentDetails?.lr_id;

    const shouldUseSWMScope = tag === ENTITY.TYPE.TEST_CASE;

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source: [{ entityType: sourceType, entityId: sourceId }],
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseSWMScope
        ? {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
          scopeEntities: [
            {
              entityType: ENTITY.TYPE.SWM,
              entityId: Number(selectedSWMMain?.swm_id),
            },
          ],
        }
        : {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
        },
    });
    setDisableSmartMatch(false);
  };

  const handleLinkSuggestions = (recommendation) => {
    if (recommendation.type === "SWM") {
      setSelectedSWMMain(recommendation);
      getSWMLinkedLRList({
        eeId: recommendation?.ee_id,
        swmId: recommendation?.swm_id,
      });
      if (selectedLegalMain?.lr_id && recommendation?.ee_id) {
        getDisabledSWMListForLR({
          lrId: selectedLegalMain.lr_id,
          eeId: recommendation.ee_id,
        });
      }
      setOpenAISuggestion(false);
    } else if (recommendation.type === "LEGAL_REQUIREMENT") {
      handleLegalAddClick(recommendation);
      setOpenAISuggestion(false);
    } else if (recommendation.type === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  const handleLegalAddClick = (recommendation) => {
    fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...legalRequirements.get_legal_requirements_details_new,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain(legalRequirementData);
      setSelectedLegalTechUserDetails(legalRequirementData.tech_service_users);
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    fetchTestCaseRecommendationContext({
      manual_test_case_id: recommendation.manual_test_case_id,
    });
  };

  const fetchTestCaseRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...testCasesActions.get_test_cases,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      if (testCaseData?.is_campaign_linked === false) {
        openCampaignDialogForTestCases([testCaseData]);
      } else {
        setSelectedSmartMatchTestCaseSuggestions((prev) => {
          if (
            prev.some((tc) => tc.test_case_id === testCaseData.test_case_id)
          ) {
            return prev;
          }
          return [...prev, testCaseData];
        });
      }
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      let resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_users_list_pa,
          show_toast: false,
          params: { page_number: 1, page_size: 500 },
        }),
      );
      setAllUserList(resp.data);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  const getSWMLinkedLRList = async ({ eeId, swmId }) => {
    if (eeId) {
      let apiDetails = softwareIntersectionAPI.get_software_legal_requirements;

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            ee_id: Number(eeId),
            ...(swmId && { swm_id: Number(swmId) }),
          },
          show_toast: false,
        }),
      );

      if (resp.error) {
        setDisabledLegalRequirement([]);
      } else {
        let linkedLrIds = [];
        if (resp.data[0]?.linked_lr_details?.[0]?.lr_id) {
          linkedLrIds = resp?.data?.[0]?.linked_lr_details?.map((lr) =>
            Number(lr?.lr_id),
          );
        }

        setDisabledLegalRequirement(linkedLrIds);
      }
    }
  };

  const getDisabledSWMListForLR = async ({ lrId, eeId }) => {
    if (!lrId || !eeId) {
      setDisabledSWMComponents([]);
      return;
    }

    if (
      !selectedProjectDetails?.project_id ||
      !selectedVariantDetails?.variant_id
    ) {
      setDisabledSWMComponents([]);
      return;
    }

    const resp = await dispatch(
      HandleApiActions({
        ...softwareIntersectionAPI.get_swm_for_legal_requirements,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          ee_id: Number(eeId),
          lr_id: Number(lrId),
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (resp.error) {
      setDisabledSWMComponents([]);
      return;
    }

    const swmIds = resp?.data?.[0]?.swm_ids || [];
    const normalizedIds = swmIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setDisabledSWMComponents(normalizedIds);
  };
  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
  }, [selectedProjectDetails, selectedVariantDetails]);

  const clearForm = () => {
    setFormResetKey((k) => k + 1);
    setSelectedSWMMain(null);
    setSelectedLegalMain(null);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestor([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedComplianceSupplier([]);
    setDisabledSWMComponents([]);
    clearCampaignLinkFields();
  };

  //This code will refresh the test case
  const resetTestCaseState = () => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  };

  useEffect(() => {
    resetTestCaseState();
  }, [selectedSWMMain, selectedLegalMain]);

  useEffect(() => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  }, [selectedSWMMain, selectedLegalMain]);

  useEffect(() => {
    if (!selectedLegalMain?.lr_id) {
      setDisabledSWMComponents([]);
    }
  }, [selectedLegalMain]);
  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                <Box>
                  <SelectTemplate
                    heading={"Select Software Module"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedSWMMain !== null}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain !== null}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.SWM,
                      )
                    }
                    content={
                      selectedSWMMain ? (
                        <Box sx={{ pl: 2 }}>
                          <KeyValueDisplayCard
                            data={[
                              {
                                key: "Name",
                                value: selectedSWMMain.name,
                                style: { fontWeight: 600 },
                              },
                              {
                                key: "Description",
                                value: selectedSWMMain.description,
                              },
                              {
                                key: "Version",
                                value: selectedSWMMain.version,
                              },
                            ]}
                          />{" "}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={selectedLegalMain !== null}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedSWMMain !== null}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedSWMMain,
                        ENTITY.TYPE.LEGAL,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "left",
                            pl: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Standard:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Name: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details?.name
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.number
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Revision: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.revision
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Requirement:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.req_number
                              : ""
                              }`}
                          </Typography>

                          <CustomToolTip
                            length={200}
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Description: ${selectedLegalMain
                              ? selectedLegalMain.description
                              : ""
                              }`}
                          </CustomToolTip>
                        </Box>
                      </Box>
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    disableSmartMatch={disableSmartMatch}
                    addButtonDisabled={
                      selectedLegalMain === null || selectedSWMMain === null
                    }
                    enableEdit={
                      selectedTestCaseForDisplay !== null &&
                      selectedTestCaseForDisplay.length > 0
                    }
                    showSmartMatch={
                      selectedSWMMain !== null && selectedLegalMain !== null
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay &&
                          selectedTestCaseForDisplay.length &&
                          selectedTestCaseForDisplay.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTextCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentSelection =
                                      selectedTextCaseMain || [];
                                    if (
                                      e.target.checked &&
                                      item?.is_campaign_linked === false
                                    ) {
                                      openCampaignDialogForTestCases([item]);
                                      return;
                                    }
                                    const newSelectedTestCase = e.target.checked
                                      ? currentSelection.some(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id ===
                                          item?.test_case_id,
                                      )
                                        ? currentSelection
                                        : [...currentSelection, item]
                                      : currentSelection.filter(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id !==
                                          item?.test_case_id,
                                      );
                                    setSelectedTestCaseMain(
                                      newSelectedTestCase,
                                    );
                                    setSelectedTestCaseForDisplay(
                                      newSelectedTestCase,
                                    );
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              >
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestor}
                selectedComplianceReviewers={selectedComplianceReviewers}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                selectedComponent={selectedSWMMain}
                description={description}
                formType={entityType}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                localSubmitDisabled={
                  selectedSWMMain === null ||
                  selectedLegalMain === null ||
                  description === "" ||
                  description.trim().length < 3 ||
                  selectedComplianceRequestors.length === 0 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={() => {
                  const existingSWM = savedLinkages.find(
                    (item) => item.swm_id === selectedSWMMain?.swm_id,
                  );

                  if (existingSWM) {
                    const duplicateLR = existingSWM.legal.find(
                      (legal) => legal.lr_id === selectedLegalMain?.lr_id,
                    );
                    if (duplicateLR) {
                      alert("Unable to create duplicate ticket");
                      return;
                    }
                    existingSWM.legal.push({
                      lr_id: selectedLegalMain?.lr_id,
                      description: description,
                      oem_users: selectedComplianceRequestors.map(
                        (user) => user.value,
                      ),
                      tech_service_users: selectedComplianceReviewers.map(
                        (user) => user.value,
                      ),
                      suppliers: selectedComplianceSupplier.map(
                        (user) => user.value,
                      ),

                      test_case_ids:
                        selectedTextCaseMain?.map((tc) => tc.test_case_id) ||
                        [],
                      test_cases: selectedTextCaseMain || [],
                      ...(showDynamicSelect
                        ? { dynamic_form: dynamicFormFilteredData }
                        : {}),
                    });
                    setSavedLinkages([...savedLinkages]);
                  } else {
                    setSavedLinkages([
                      ...savedLinkages,
                      {
                        swm_id: selectedSWMMain?.swm_id,
                        addOn: selectedSWMMain,
                        ee_id: selectedSWMMain?.ee_id,
                        legal: [
                          {
                            lr_id: selectedLegalMain?.lr_id,
                            description: description,
                            oem_users: selectedComplianceRequestors.map(
                              (user) => user.value,
                            ),
                            tech_service_users: selectedComplianceReviewers.map(
                              (user) => user.value,
                            ),
                            suppliers: selectedComplianceSupplier.map(
                              (user) => user.value,
                            ),

                            test_case_ids:
                              selectedTextCaseMain?.map(
                                (tc) => tc.test_case_id,
                              ) || [],
                            test_cases: selectedTextCaseMain || [],
                            ...(showDynamicSelect
                              ? { dynamic_form: dynamicFormFilteredData }
                              : {}),
                          },
                        ],
                      },
                    ]);
                  }
                  clearForm();
                }}
                savedLinkageCard={
                  <EESWMSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    entityType={"swm"}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disabledSWMComponents={disabledSWMComponents}
        onSelectEEForSWM={(eeData) => {
          if (selectedLegalMain?.lr_id && eeData?.ee_id) {
            getDisabledSWMListForLR({
              lrId: selectedLegalMain.lr_id,
              eeId: eeData.ee_id,
            });
          } else {
            setDisabledSWMComponents([]);
          }
        }}
        disableLrRadioButton={(item) => {
          const eeExists = savedLinkages?.find(
            (linkage) => linkage.ee_id === selectedSWMMain?.ee_id,
          );
          return eeExists
            ? eeExists.legal?.some((legal) => legal.lr_id === item.lr_id)
            : false;
        }}
        selectedSWMMain={selectedSWMMain}
        onClickAdd={(data) => {
          setSelectedSWMMain(data);
          getSWMLinkedLRList({
            eeId: data.ee_id,
            swmId: data.swm_id,
          });
          setOpenEESelect(false);

          const removeDuplicates = (arr = []) => {
            if (!Array.isArray(arr)) return [];
            const seen = new Set();
            return arr.filter((user) => {
              if (!user?.user_id || seen.has(user.user_id)) {
                return false;
              }
              seen.add(user.user_id);
              return true;
            });
          };

          const insertCapabilityCodeToUserList = (userDetails) => {
            if (!userDetails || !Array.isArray(userDetails)) return [];
            let capabilityCodeAddedUser = userDetails.map((u) => {
              for (let au of allUsersList) {
                if (au.user_id == u.user_id) {
                  return {
                    ...u,
                    capability_code: au.role_details.capability_code,
                  };
                }
              }
              return u;
            });
            return capabilityCodeAddedUser;
          };
          const ownerDetails = data?.owner_details
            ? insertCapabilityCodeToUserList(data?.owner_details)
            : [];
          const coOwnerDetails = data?.co_owners
            ? insertCapabilityCodeToUserList(data?.co_owner_details)
            : [];
          const editorDetails = data?.edit_user_details
            ? insertCapabilityCodeToUserList(data?.edit_user_details)
            : [];
          const viewersDetails = data?.view_user_details
            ? insertCapabilityCodeToUserList(data?.view_user_details)
            : [];
          let duplicateRemovedComponentRequestor = removeDuplicates([
            ...ownerDetails,
            ...coOwnerDetails,
          ]);
          setSelectedComponentRequestor(
            duplicateRemovedComponentRequestor.filter((ele) => {
              return ele.capability_code !== CAPABILITY_CODE.supplier;
            }),
          );

          let duplicateRemovedComponentSupplierDetails = removeDuplicates([
            ...coOwnerDetails,
            ...editorDetails,
            ...viewersDetails,
          ]);
          setSelectedComponentSupplierDetails(
            duplicateRemovedComponentSupplierDetails.filter((ele) => {
              return ele.capability_code === CAPABILITY_CODE.supplier;
            }),
          );
        }}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        setSelectedLegalMain={setSelectedLegalMain}
        disabledLegalRequirement={disabledLegalRequirement}
        onClickAddLegal={(data) => {
          setSelectedLegalMain(data);
          setOpenLegalSelect(false);
          setSelectedLegalTechUserDetails(data.tech_service_users);
        }}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTextCaseMain}
        setSelectedTestCaseMain={setSelectedTestCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityID={selectedSWMMain?.swm_id}
        entityType={"swm"}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={() => { }}
          closeDialogOnAdd={closeAiDialogOnAdd}
        />
      )}
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={commitSelectedTestCases}
      />
    </Box>
  );
}

export default RaiseSoftwareTickets;
